import { boolean, object, string } from "yup";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Link,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { capitalize } from "portal/utils/strings";
import { classes } from "portal/theme/theme";
import { ConfigType } from "protos/config/api/config_service";
import {
  cropsBasicReadStatement,
  useCanPinModels,
  useCanReadAdvancedCrops,
  useCanUpdateAdvancedCrops,
  useCanViewInVeselka,
} from "portal/views/robots/crops/permissions";
import { DisplayCrop, getConfigKey } from "portal/utils/crops";
import { encode } from "ngeohash";
import { Field, Form, Formik } from "formik";
import { TextField as FormikTextField } from "formik-mui";
import { getModelinatorId } from "portal/utils/almanac";
import { getModelinatorPath } from "portal/utils/routing";
import { isProduction } from "portal/utils/environment";
import { isUndefined } from "portal/utils/identity";
import { ModelinatorConfig } from "protos/almanac/almanac";
import { portalApi } from "portal/state/portalApi";
import { Link as RouterLink } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import { SwitchWithLabel } from "portal/components/SwitchWithLabel";
import { toQuery } from "common/utils/browser";
import { useJigsawMetrics } from "../utils/hooks/useJigsawMetrics";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import { ViewPlaceholder } from "./ViewPlaceholder";
import { withAuthorizationRequired } from "./auth/WithAuthorizationRequired";
import { withErrorBoundary } from "portal/components/ErrorBoundary";
import PinnedIcon from "@mui/icons-material/PushPin";
import React, { FunctionComponent, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import RecommendedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import remarkGfm from "remark-gfm";
import SaveIcon from "@mui/icons-material/SaveOutlined";
import UnpinnedIcon from "@mui/icons-material/PushPinOutlined";
import VeselkaIcon from "portal/images/icons/veselka.svg?react";

type EditableDisplayCrop = Omit<
  DisplayCrop,
  "id" | "carbonName" | "localeName" | "commonName"
>;

type ReasonDialogBehavior = {
  newValue: Partial<EditableDisplayCrop>;
  exitOnUpdate: boolean;
};

interface Props {
  serial?: string;
  configCrop?: DisplayCrop | undefined;
  open?: boolean;
  onClose: () => void;
}

const _CropEditor: FunctionComponent<Props> = ({
  serial,
  configCrop,
  open = false,
  onClose,
}) => {
  const { i18n, t } = useTranslation();
  const useJigsaw = useJigsawMetrics();

  const canReadAdvanced = useCanReadAdvancedCrops();
  const canUpdateAdvanced = useCanUpdateAdvancedCrops();
  const canPinModels = useCanPinModels();
  const canOpenCrop = canReadAdvanced || canUpdateAdvanced || canPinModels;
  const canViewInVeselka = useCanViewInVeselka();

  const [pendingUpdate, setPendingUpdate] = useState<
    ReasonDialogBehavior | undefined
  >(undefined);

  const [setConfigValue] = useMutationPopups(
    portalApi.endpoints.setConfigValue.useMutation(),
    {
      success: capitalize(
        t("utils.actions.updatedLong", {
          subject: t("models.configs.config_one"),
        })
      ),
    }
  );
  const { data: robotSummary, isLoading: isRobotLoading } = useQueryPopups(
    portalApi.endpoints.getRobot.useQuery(
      serial ? { language: i18n.language, serial, useJigsaw } : skipToken
    )
  );

  const geo4 = useMemo<string>(() => {
    const geohashOptions = ["NONE"]; // always include non-geo models
    const { x, y } = robotSummary?.robot?.health?.location ?? {};
    if (!isUndefined(x) && !isUndefined(y)) {
      const geohash = encode(x, y, 4);
      geohashOptions.unshift(geohash);
    }
    return geohashOptions.join(",");
  }, [robotSummary?.robot?.health?.location]);

  const { data: veselkaCrop } = useQueryPopups(
    portalApi.endpoints.getCrop.useQuery(
      configCrop ? { cropId: configCrop.id } : skipToken
    )
  );

  const {
    data: modelinators,
    isLoading,
    isSuccess,
  } = useQueryPopups(
    portalApi.endpoints.listModelinatorsForRobotAndCrop.useQuery(
      serial && configCrop ? { serial, cropId: configCrop.id } : skipToken
    )
  );

  if (!canOpenCrop || !serial || !open) {
    return;
  }

  const hasNotes = veselkaCrop?.description || veselkaCrop?.notes;

  const applyUpdate = async (
    updated: Partial<EditableDisplayCrop>,
    reason: string,
    exitOnUpdate: boolean
  ): Promise<void> => {
    if (!configCrop) {
      return;
    }
    await Promise.all([
      updated.isEnabled === undefined ||
      updated.isEnabled === configCrop.isEnabled
        ? undefined
        : setConfigValue({
            serial,
            path: [getConfigKey(serial, configCrop.id), "enabled"].join("/"),
            value: updated.isEnabled,
            reason,
            configType: ConfigType.BOOL,
          }),
      updated.pinned === undefined || updated.pinned === configCrop.pinned
        ? undefined
        : setConfigValue({
            serial,
            path: [getConfigKey(serial, configCrop.id), "pinned_model"].join(
              "/"
            ),
            value: updated.pinned.trim(),
            reason,
            configType: ConfigType.STRING,
          }),
      updated.recommended === undefined ||
      updated.recommended === configCrop.recommended
        ? undefined
        : setConfigValue({
            serial,
            path: [
              getConfigKey(serial, configCrop.id),
              "recommended_model",
            ].join("/"),
            value: updated.recommended.trim(),
            reason,
            configType: ConfigType.STRING,
          }),
    ]);
    setPendingUpdate(undefined);
    if (exitOnUpdate) {
      onClose();
    }
  };

  return (
    <>
      <ReasonDialog
        onSubmit={applyUpdate}
        behavior={pendingUpdate}
        onCancel={() => setPendingUpdate(undefined)}
      />
      <Drawer
        variant="permanent"
        anchor="right"
        classes={{
          paper: classes(
            "h-auto p-4 bottom-0 flex flex-col gap-4",
            "top-0 md:top-16",
            "w-screen md:w-80"
          ),
        }}
      >
        <Formik<EditableDisplayCrop>
          enableReinitialize
          initialValues={{
            isEnabled: configCrop?.isEnabled ?? false,
            pinned: configCrop?.pinned ?? "",
            recommended: configCrop?.recommended ?? "",
          }}
          validationSchema={object({
            isEnabled: boolean().required(),
            pinned: string(),
            recommended: string(),
          })}
          onSubmit={async (updated) => {
            if (!configCrop) {
              return;
            }
            if (
              updated.pinned !== configCrop.pinned ||
              updated.recommended !== configCrop.recommended
            ) {
              // changes requiring a reason
              setPendingUpdate({ newValue: updated, exitOnUpdate: true });
            } else {
              await applyUpdate(updated, "", true);
            }
          }}
        >
          {({ submitForm, isSubmitting, dirty }) => (
            <Form className="flex flex-col items-justify gap-4">
              <div className="flex justify-end gap-4">
                <Button variant="text" onClick={onClose} color="inherit">
                  {t("utils.actions.cancel")}
                </Button>
                <Button
                  color="secondary"
                  variant="contained"
                  disabled={!dirty}
                  loading={isSubmitting}
                  onClick={submitForm}
                  startIcon={<SaveIcon />}
                >
                  {t("utils.actions.update")}
                </Button>
              </div>
              {canReadAdvanced && (
                <TextField
                  disabled
                  value={configCrop?.id ?? t("components.Loading.placeholder")}
                  label="Crop ID"
                  slotProps={{
                    input: {
                      className: "font-mono text-xs",
                    },
                  }}
                />
              )}
              <TextField
                disabled
                value={
                  configCrop?.localeName ?? t("components.Loading.placeholder")
                }
                label="Name"
                slotProps={{
                  input: {
                    className: "font-mono text-xs",
                  },
                }}
              />
              {canUpdateAdvanced && (
                <Field
                  component={SwitchWithLabel}
                  type="checkbox"
                  name="isEnabled"
                  disabled={!configCrop?.isEnabled && veselkaCrop?.archived}
                  label={t("utils.descriptors.enabled")}
                />
              )}
              {hasNotes && (
                <>
                  <Divider />
                  <Typography variant="h6">
                    {t("models.crops.fields.notes")}
                  </Typography>
                  <Typography variant="body1">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {veselkaCrop.description}
                    </ReactMarkdown>
                  </Typography>
                  <Typography variant="body1">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {veselkaCrop.notes}
                    </ReactMarkdown>
                  </Typography>
                </>
              )}
              {(canReadAdvanced || canPinModels) && (
                <>
                  <Divider />
                  <div className="flex justify-between">
                    <Typography variant="h6">
                      {capitalize(t("models.models.model_other"))}
                    </Typography>
                    {canViewInVeselka && (
                      <Button
                        variant="outlined"
                        size="small"
                        color="inherit"
                        LinkComponent="a"
                        className={classes("flex gap-1")}
                        target="_blank"
                        href={`${
                          window._jsenv.REACT_APP_VESELKA_URL
                        }/react/models/models?${toQuery({
                          deploy: "True",
                          isTraining: "False",
                          deploymentCrops: configCrop?.id,
                          sort_by: "created",
                          sort_order: "desc",
                          environment: isProduction
                            ? "production"
                            : "development",
                          geohashPrefixes: geo4,
                        })}`}
                        startIcon={<VeselkaIcon />}
                        loading={isRobotLoading}
                      >
                        <span className="group-hover:text-rainbow">
                          {t("components.CropEditor.viewIn")}
                        </span>
                      </Button>
                    )}
                  </div>
                </>
              )}
              {canPinModels && (
                <Field
                  component={FormikTextField}
                  name="pinned"
                  label={t("models.crops.fields.pinned")}
                  slotProps={{
                    input: {
                      className: "font-mono",
                    },
                  }}
                />
              )}
              {canReadAdvanced && (
                <Field
                  component={FormikTextField}
                  name="recommended"
                  label={t("models.crops.fields.recommended")}
                  slotProps={{
                    input: {
                      className: "font-mono",
                    },
                  }}
                  disabled={!canUpdateAdvanced}
                />
              )}
              {(canReadAdvanced || canPinModels) && (
                <>
                  {isLoading && (
                    <ViewPlaceholder
                      text={t("components.Loading.placeholder")}
                    />
                  )}
                  {isSuccess && modelinators.length === 0 && (
                    <ViewPlaceholder text="No models for crop" />
                  )}
                  <ul className="list-none p-0 m-0 flex flex-col gap-4">
                    {modelinators?.map((modelinator) => (
                      <Modelinator
                        serial={serial}
                        key={getModelinatorId(serial, modelinator)}
                        modelinator={modelinator}
                        recommended={
                          modelinator.modelId === configCrop?.recommended
                        }
                        pinned={modelinator.modelId === configCrop?.pinned}
                        onPinnedChange={(pinned) => {
                          setPendingUpdate({
                            newValue: { pinned },
                            exitOnUpdate: false,
                          });
                        }}
                      />
                    ))}
                  </ul>
                </>
              )}
            </Form>
          )}
        </Formik>
      </Drawer>
    </>
  );
};

interface ReasonDialogProps {
  commentText?: string;
  behavior: ReasonDialogBehavior | undefined;
  onSubmit: (
    updated: Partial<EditableDisplayCrop>,
    reason: string,
    exitOnUpdate: boolean
  ) => Promise<void>;
  onCancel: () => void;
}
export const ReasonDialog: FunctionComponent<ReasonDialogProps> = ({
  onSubmit,
  onCancel,
  behavior,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={behavior !== undefined} onClose={onCancel}>
      <DialogTitle>{t("components.ChangeReasonDialog.title")}</DialogTitle>
      <Formik
        enableReinitialize
        initialValues={{
          reason: "",
        }}
        validationSchema={object({
          reason: string().required(),
        })}
        onSubmit={async (values) => {
          if (behavior === undefined) {
            onCancel();
            return;
          }
          await onSubmit(
            behavior.newValue,
            values.reason,
            behavior.exitOnUpdate
          );
        }}
      >
        {({ submitForm, isSubmitting, dirty }) => (
          <Form>
            <DialogContent className="flex flex-col gap-4 pt-2">
              <Field
                component={FormikTextField}
                name="reason"
                label={t("views.admin.config.reason")}
                required
              />
            </DialogContent>
            <DialogActions>
              <Button
                variant="text"
                className="text-white"
                disabled={isSubmitting}
                onClick={onCancel}
              >
                {t("utils.actions.cancel")}
              </Button>
              <Button
                disabled={!dirty}
                loading={isSubmitting}
                onClick={submitForm}
              >
                {t("utils.actions.submit")}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export const CropEditor = withErrorBoundary(
  { i18nKey: "components.CropEditor.failed" },
  withAuthorizationRequired(cropsBasicReadStatement, _CropEditor)
);

interface ModelinatorProps {
  serial: string;
  pinned?: boolean;
  recommended?: boolean;
  modelinator: ModelinatorConfig;
  onPinnedChange: (modelId: string) => void;
}

export const Modelinator: FunctionComponent<ModelinatorProps> = ({
  serial,
  pinned = false,
  recommended = false,
  modelinator,
  onPinnedChange,
}) => {
  const { t } = useTranslation();
  const canReadAdvanced = useCanReadAdvancedCrops();
  const canPinModels = useCanPinModels();

  return (
    <li className="flex items-center gap-2">
      {canPinModels && (
        <IconButton
          onClick={() => {
            onPinnedChange(pinned ? "" : modelinator.modelId);
          }}
        >
          {pinned ? (
            <PinnedIcon className="text-yellow-500" />
          ) : (
            <UnpinnedIcon className="text-gray-600" />
          )}
        </IconButton>
      )}
      {canReadAdvanced && recommended && (
        <Tooltip arrow title={t("models.crops.fields.recommended")}>
          <RecommendedIcon className="text-yellow-500" />
        </Tooltip>
      )}
      {canReadAdvanced ? (
        <Link
          component={RouterLink}
          color="inherit"
          className="font-mono text-xs cursor-pointer "
          to={getModelinatorPath(
            serial,
            modelinator.cropId,
            modelinator.modelId
          )}
        >
          {modelinator.modelId}
        </Link>
      ) : (
        <span className="font-mono text-xs text-white">
          {modelinator.modelId}
        </span>
      )}
    </li>
  );
};
